import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  connect() {
    // Configure Trix to hide button labels
    this.configureTrixLabels()
    
    // Wait for Trix to be fully initialized
    this.waitForTrix().then(() => {
      this.addGiphyButton()
      this.setupFormHandling()
      this.setupCustomLinkDialog()
    })
  }
  
  configureTrixLabels() {
    // Configure Trix to not show button labels
    if (typeof Trix !== 'undefined') {
      // Alternative approach: Hide labels after initialization
      document.addEventListener('trix-initialize', (event) => {
        this.hideButtonLabels(event.target)
      })
    } else {
      // If Trix isn't loaded yet, try again in a moment
      setTimeout(() => this.configureTrixLabels(), 100)
    }
  }
  
  hideButtonLabels(trixEditor) {
    const toolbar = trixEditor.previousElementSibling
    
    if (toolbar && toolbar.tagName === 'TRIX-TOOLBAR') {
      // Remove all text content from buttons, keep only icons
      const buttons = toolbar.querySelectorAll('.trix-button')
      
      buttons.forEach((button, index) => {
        // Remove text nodes but keep icon elements
        Array.from(button.childNodes).forEach(node => {
          if (node.nodeType === Node.TEXT_NODE) {
            node.remove()
          }
        })
        
        // Remove any label elements
        const labels = button.querySelectorAll('.trix-button--icon-label')
        labels.forEach(label => label.remove())
        
        // Remove title attribute to prevent tooltips
        if (button.hasAttribute('title')) {
          button.removeAttribute('title')
        }
      })
    }
  }

  waitForTrix() {
    return new Promise((resolve) => {
      const checkTrix = () => {
        const trixEditor = this.element.querySelector('trix-editor')
        if (trixEditor && trixEditor.editor) {
          // Also try to hide labels immediately when Trix is ready
          this.hideButtonLabelsAggressively()
          resolve()
        } else {
          setTimeout(checkTrix, 50)
        }
      }
      checkTrix()
    })
  }
  
  hideButtonLabelsAggressively() {
    const trixEditor = this.element.querySelector('trix-editor')
    if (trixEditor) {
      this.hideButtonLabels(trixEditor)
      
      // Try again after a short delay in case labels appear later
      setTimeout(() => {
        this.hideButtonLabels(trixEditor)
      }, 500)
      
      // And once more for good measure
      setTimeout(() => {
        this.hideButtonLabels(trixEditor)
      }, 1000)
      
      // Set up a mutation observer to catch any dynamically added labels
      this.setupLabelMutationObserver(trixEditor)
    }
  }
  
  setupLabelMutationObserver(trixEditor) {
    const toolbar = trixEditor.previousElementSibling
    if (toolbar && toolbar.tagName === 'TRIX-TOOLBAR') {
      const observer = new MutationObserver((mutations) => {
        let needsCleanup = false
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList' || mutation.type === 'characterData') {
            needsCleanup = true
          }
        })
        
        if (needsCleanup) {
          this.hideButtonLabels(trixEditor)
        }
      })
      
      observer.observe(toolbar, {
        childList: true,
        subtree: true,
        characterData: true
      })
    }
  }

  addGiphyButton() {
    const toolbar = this.element.querySelector('trix-toolbar')
    const buttonGroups = toolbar.querySelector('.trix-button-group')
    
    if (toolbar && !toolbar.querySelector('.giphy-embed-button')) {
      // Create the Giphy embed button
      const giphyButton = document.createElement('button')
      giphyButton.type = 'button'
      giphyButton.className = 'trix-button giphy-embed-button'
      giphyButton.title = 'Embed Giphy'
      giphyButton.innerHTML = '<iconify-icon icon="ph:gif" style="font-size: 20px; font-weight: bold; color: #000 !important;"></iconify-icon>'
      giphyButton.addEventListener('click', (e) => {
        e.preventDefault()
        this.showGiphyModal()
      })
      
      // Add button to the toolbar (after existing button groups)
      if (buttonGroups) {
        buttonGroups.appendChild(giphyButton)
      } else {
        toolbar.appendChild(giphyButton)
      }
    }
  }

  setupFormHandling() {
    const trixEditor = this.element.querySelector('trix-editor')
    if (!trixEditor) return

    // Handle file upload events
    trixEditor.addEventListener('trix-file-accept', (event) => {
      if (!event.attachment) return
      
      const { file } = event.attachment
      if (!file) return
      
      // Only allow images for ActionText
      if (!file.type.startsWith('image/')) {
        event.preventDefault()
        alert('Only image files are allowed')
        return
      }
      
      // Limit file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        event.preventDefault()
        alert('File size must be less than 10MB')
        return
      }
    })

    // Clean up when attachments are removed
    trixEditor.addEventListener('trix-attachment-remove', (event) => {
      if (event.attachment) {
        const attrs = event.attachment.attributes || {}
        if (attrs.url && attrs.url.startsWith('blob:')) {
          try {
            URL.revokeObjectURL(attrs.url)
          } catch (error) {
            console.warn('Error revoking blob URL:', error)
          }
        }
      }
    })

    // Ensure form submits properly
    const form = trixEditor.closest('form')
    if (form) {
      form.addEventListener('submit', (event) => {
        // Force update of the hidden field to ensure Trix content is captured
        trixEditor.dispatchEvent(new Event('trix-change', { bubbles: true }))
        console.log('Form submitting with ActionText content')
      })
    }
  }

  showGiphyModal() {
    // Create modal HTML using native dialog element
    const modalHTML = `
      <dialog class="giphy-modal modal">
        <div class="modal-box">
          <h3 class="text-lg font-semibold mb-4 flex items-center gap-2">
            <span style="font-size: 20px;">🎬</span>
            Embed Giphy GIF
          </h3>
          <div class="space-y-4">
            <div>
              <label class="label">
                <span class="label-text font-medium">Giphy URL or Embed Code</span>
              </label>
              <textarea 
                class="textarea textarea-bordered w-full" 
                placeholder="Paste any of the following formats..."
                rows="4"></textarea>
              <div class="label">
                <span class="label-text-alt">
                  <strong>Supported formats:</strong><br/>
                  • Giphy page URL: https://giphy.com/gifs/title-abc123<br/>
                  • Direct GIF URL: https://i.giphy.com/abc123.gif<br/>
                  • Embed code: &lt;iframe src="..."&gt;...&lt;/iframe&gt;<br/>
                  • Short URL: https://gph.is/abc123
                </span>
              </div>
            </div>
            <div class="modal-action">
              <button type="button" class="btn btn-ghost cancel-btn">Cancel</button>
              <button type="button" class="btn btn-primary embed-btn">
                <span style="font-size: 16px; margin-right: 4px;">🎬</span>
                Embed GIF
              </button>
            </div>
          </div>
        </div>
      </dialog>
    `
    
    // Add modal to page
    document.body.insertAdjacentHTML('beforeend', modalHTML)
    const modal = document.querySelector('.giphy-modal')
    const textarea = modal.querySelector('textarea')
    const cancelBtn = modal.querySelector('.cancel-btn')
    const embedBtn = modal.querySelector('.embed-btn')
    
    // Show the modal using native dialog API
    modal.showModal()
    
    // Focus the textarea
    textarea.focus()
    
    // Event handlers
    const closeModal = () => {
      modal.close()
      modal.remove()
    }
    
    cancelBtn.addEventListener('click', closeModal)
    
    embedBtn.addEventListener('click', () => {
      const input = textarea.value.trim()
      if (input) {
        this.insertGiphyEmbed(input)
      }
      closeModal()
    })
    
    // Close on background click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        closeModal()
      }
    })
  }

  insertGiphyEmbed(input) {
    const trixEditor = this.element.querySelector('trix-editor')
    
    // Handle various input types
    let giphyUrl = null
    let gifUrl = null
    let giphyId = null
    
    // Check if it's a direct iframe embed code
    if (input.includes('<iframe') && input.includes('giphy.com')) {
      // Extract src from iframe
      const srcMatch = input.match(/src="([^"]*)"/)
      if (srcMatch) {
        giphyUrl = srcMatch[1]
        giphyId = this.extractGiphyId(giphyUrl)
      }
    }
    // Check if it's a Giphy URL
    else if (input.includes('giphy.com') || input.includes('gph.is')) {
      giphyUrl = input
      giphyId = this.extractGiphyId(input)
    }
    // Check if it's already a direct GIF URL
    else if (input.includes('i.giphy.com') && input.endsWith('.gif')) {
      gifUrl = input
      giphyId = this.extractGiphyId(input)
    }
    
    if (giphyId) {
      try {
        // Generate the direct GIF URL if we don't have it
        if (!gifUrl) {
          gifUrl = `https://i.giphy.com/${giphyId}.gif`
        }
        
        // Create an attachment-like object for Trix
        const gifTitle = `Giphy GIF (${giphyId})`
        
        // Method 1: Try inserting as an image if possible
        try {
          // Insert line break first
          trixEditor.editor.insertString('\n')
          
          // Create an image element that Trix can handle
          const img = document.createElement('img')
          img.src = gifUrl
          img.alt = gifTitle
          img.title = gifTitle
          img.style.maxWidth = '100%'
          img.style.height = 'auto'
          img.style.borderRadius = '8px'
          
          // Insert the image HTML directly
          trixEditor.editor.insertHTML(img.outerHTML)
          
          // Insert another line break
          trixEditor.editor.insertString('\n')
          
        } catch (imageError) {
          console.warn('Could not insert as image, falling back to link:', imageError)
          
          // Method 2: Fallback to rich link with preview
          trixEditor.editor.insertString('\n')
          const linkHtml = `
            <div style="border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; margin: 8px 0; background: #f9fafb;">
              <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
                <span style="font-size: 20px;">🎬</span>
                <strong>Giphy GIF</strong>
              </div>
              <img src="${gifUrl}" alt="${gifTitle}" style="max-width: 100%; height: auto; border-radius: 4px; margin-bottom: 8px;" />
              <a href="${giphyUrl || gifUrl}" target="_blank" style="color: #3b82f6; text-decoration: underline;">View on Giphy</a>
            </div>
          `
          trixEditor.editor.insertHTML(linkHtml)
          trixEditor.editor.insertString('\n')
        }
        
      } catch (error) {
        console.error('Error inserting GIF:', error)
        alert('Error inserting GIF: ' + error.message)
      }
    } else {
      alert('Please enter a valid Giphy URL, embed code, or direct GIF URL.\n\nExamples:\n• https://giphy.com/gifs/title-abc123\n• https://i.giphy.com/abc123.gif\n• <iframe src="https://giphy.com/embed/abc123">...</iframe>')
    }
  }

  extractGiphyId(url) {
    if (!url || typeof url !== 'string') return null
    
    // Extract Giphy ID from various URL formats
    const patterns = [
      // Standard Giphy URLs with title-ID format
      /giphy\.com\/gifs\/.*?([a-zA-Z0-9]{8,})(?:\?|#|$)/,
      // Direct embed URLs
      /giphy\.com\/embed\/([a-zA-Z0-9]{8,})/,
      // Media URLs
      /media\.giphy\.com\/media\/([a-zA-Z0-9]{8,})/,
      // Direct GIF URLs
      /i\.giphy\.com\/([a-zA-Z0-9]{8,})\.gif/,
      // Short URLs
      /gph\.is\/([a-zA-Z0-9]{8,})/,
      // Alternative formats
      /giphy\.com\/gifs\/([a-zA-Z0-9]{8,})/
    ]
    
    for (const pattern of patterns) {
      const match = url.match(pattern)
      if (match && match[1]) {
        // For URLs with title-ID format, take the last part after last dash
        let id = match[1]
        if (id.includes('-')) {
          const parts = id.split('-')
          id = parts[parts.length - 1]
        }
        // Ensure the ID is at least 8 characters (typical Giphy ID length)
        if (id.length >= 8) {
          return id
        }
      }
    }
    
    return null
  }

  setupCustomLinkDialog() {
    const trixEditor = this.element.querySelector('trix-editor')
    if (!trixEditor) return

    const toolbar = trixEditor.previousElementSibling
    if (!toolbar || toolbar.tagName !== 'TRIX-TOOLBAR') return

    // Find where the default link button would be (it's hidden via CSS)
    const defaultLinkButton = toolbar.querySelector('[data-trix-attribute="href"]')
    
    // Find the button group where the link button should be
    const buttonGroup = defaultLinkButton ? defaultLinkButton.closest('.trix-button-group') : toolbar.querySelector('.trix-button-group')
    if (!buttonGroup) return

    // Create our custom link button with iconify (same as Giphy button)
    const customLinkButton = document.createElement('button')
    customLinkButton.type = 'button'
    customLinkButton.className = 'trix-button custom-link-button'
    customLinkButton.tabIndex = -1
    customLinkButton.title = 'Link'
    customLinkButton.innerHTML = '<iconify-icon icon="ph:link" style="font-size: 16px; color: #000 !important;"></iconify-icon>'

    // Insert our button where the default one would be
    if (defaultLinkButton && defaultLinkButton.parentElement) {
      defaultLinkButton.parentElement.insertBefore(customLinkButton, defaultLinkButton)
    } else {
      buttonGroup.appendChild(customLinkButton)
    }

    // Add our click handler
    customLinkButton.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      
      // Get current selection and any existing link
      const selectedRange = trixEditor.editor.getSelectedRange()
      const selectedText = trixEditor.editor.getSelectedDocument().toString()
      const attributes = trixEditor.editor.getDocument().getCommonAttributesAtRange(selectedRange)
      const currentHref = attributes.href || ''
      
      // Toggle active state visually
      const hasLink = !!currentHref
      if (hasLink) {
        customLinkButton.classList.add('trix-active')
      } else {
        customLinkButton.classList.remove('trix-active')
      }
      
      this.showLinkDialog(currentHref, selectedText)
    })
    
    // Update active state when selection changes
    trixEditor.addEventListener('trix-selection-change', () => {
      const selectedRange = trixEditor.editor.getSelectedRange()
      const attributes = trixEditor.editor.getDocument().getCommonAttributesAtRange(selectedRange)
      const currentHref = attributes.href || ''
      
      if (currentHref) {
        customLinkButton.classList.add('trix-active')
      } else {
        customLinkButton.classList.remove('trix-active')
      }
    })
  }

  showLinkDialog(currentHref = '', selectedText = '') {
    // Create modal HTML using native dialog element
    const modalHTML = `
      <dialog class="link-dialog-modal modal">
        <div class="modal-box">
          <h3 class="text-lg font-semibold mb-4 flex items-center gap-2">
            <iconify-icon icon="ph:link" class="text-lg"></iconify-icon>
            ${currentHref ? 'Edit Link' : 'Insert Link'}
          </h3>
          <div class="space-y-4">
            <div>
              <label class="label">
                <span class="label-text font-medium">Link URL</span>
              </label>
              <input 
                type="url"
                class="input input-bordered w-full link-url-input" 
                placeholder="https://example.com"
                value="${currentHref}"
                autocomplete="off"
              />
            </div>
            ${!selectedText ? `
            <div>
              <label class="label">
                <span class="label-text font-medium">Link Text</span>
              </label>
              <input 
                type="text"
                class="input input-bordered w-full link-text-input" 
                placeholder="Link text"
                autocomplete="off"
              />
            </div>
            ` : ''}
            <div class="modal-action">
              ${currentHref ? '<button type="button" class="btn btn-error btn-outline remove-link-btn">Remove Link</button>' : ''}
              <button type="button" class="btn btn-ghost cancel-link-btn">Cancel</button>
              <button type="button" class="btn btn-primary insert-link-btn">
                <iconify-icon icon="ph:link" class="text-lg"></iconify-icon>
                ${currentHref ? 'Update' : 'Insert'}
              </button>
            </div>
          </div>
        </div>
      </dialog>
    `
    
    // Add modal to page
    document.body.insertAdjacentHTML('beforeend', modalHTML)
    const modal = document.querySelector('.link-dialog-modal')
    const urlInput = modal.querySelector('.link-url-input')
    const textInput = modal.querySelector('.link-text-input')
    const cancelBtn = modal.querySelector('.cancel-link-btn')
    const insertBtn = modal.querySelector('.insert-link-btn')
    const removeBtn = modal.querySelector('.remove-link-btn')
    
    // Show the modal using native dialog API
    modal.showModal()
    
    // Focus the URL input
    urlInput.focus()
    urlInput.select()
    
    // Event handlers
    const closeModal = () => {
      modal.close()
      modal.remove()
    }
    
    cancelBtn.addEventListener('click', closeModal)
    
    insertBtn.addEventListener('click', () => {
      const url = urlInput.value.trim()
      const text = textInput ? textInput.value.trim() : selectedText
      
      if (url) {
        this.insertLink(url, text)
      }
      closeModal()
    })
    
    if (removeBtn) {
      removeBtn.addEventListener('click', () => {
        this.removeLink()
        closeModal()
      })
    }
    
    // Insert on Enter key
    urlInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        insertBtn.click()
      }
    })
    
    if (textInput) {
      textInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          insertBtn.click()
        }
      })
    }
    
    // Close on background click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        closeModal()
      }
    })
  }

  insertLink(url, text) {
    const trixEditor = this.element.querySelector('trix-editor')
    if (!trixEditor) return

    const editor = trixEditor.editor
    const selectedRange = editor.getSelectedRange()
    
    // If there's no text and no selection, insert the URL as text
    if (!text && selectedRange[0] === selectedRange[1]) {
      text = url
    }
    
    // If we have text but no selection, insert new text with link
    if (text && selectedRange[0] === selectedRange[1]) {
      editor.insertString(text)
      const newRange = [selectedRange[0], selectedRange[0] + text.length]
      editor.setSelectedRange(newRange)
    }
    
    // Apply the link attribute
    editor.activateAttribute('href', url)
    
    // Move cursor to end
    const endPosition = editor.getSelectedRange()[1]
    editor.setSelectedRange(endPosition)
  }

  removeLink() {
    const trixEditor = this.element.querySelector('trix-editor')
    if (!trixEditor) return

    trixEditor.editor.deactivateAttribute('href')
  }
} 